import React, { useState, useEffect } from 'react';
import { StoreHookupType, StoreHookupTypeCreate, StoreHookupTypeUpdate, HookupType, HOOKUP_ICONS, HOOKUP_LABELS } from '../../../types/site';
import { siteService } from '../../../services/siteService';
import { authService } from '../../../services/auth/authService';
import Modal from '../../shared/Modal';

const HOOKUP_TYPES: HookupType[] = ['water', 'sewage', 'electricity', 'cable', 'wifi'];

const HookupTypesCard: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [hookupTypes, setHookupTypes] = useState<StoreHookupType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingHookup, setEditingHookup] = useState<StoreHookupType | null>(null);
  const [editForm, setEditForm] = useState({ display_name: '', default_amp: '', display_order: '', active: true });
  const [saving, setSaving] = useState(false);

  // Add modal state
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addForm, setAddForm] = useState({
    hookup_type: 'water' as HookupType,
    display_name: '',
    default_amp: '',
    display_order: '',
    active: true
  });

  const storeId = authService.getStoreId();

  useEffect(() => {
    loadHookupTypes();
  }, []);

  const loadHookupTypes = async () => {
    try {
      setLoading(true);
      // Pass activeOnly=false to show both active and inactive hookup types
      const types = await siteService.getStoreHookupTypes(false);
      setHookupTypes(types);
      setError(null);
    } catch (err) {
      setError('Failed to load hookup types');
      console.error('Error loading hookup types:', err);
    } finally {
      setLoading(false);
    }
  };

  const getHookupIcon = (hookupType: string): string => {
    return HOOKUP_ICONS[hookupType as keyof typeof HOOKUP_ICONS] || '⚙️';
  };

  const getHookupLabel = (hookupType: string): string => {
    return HOOKUP_LABELS[hookupType as keyof typeof HOOKUP_LABELS] || hookupType;
  };

  const handleDelete = async (hookupId: number, displayName: string) => {
    if (!window.confirm(`Are you sure you want to delete "${displayName}"? This cannot be undone.`)) {
      return;
    }
    try {
      await siteService.deleteStoreHookupType(hookupId);
      await loadHookupTypes();
    } catch (err) {
      console.error('Error deleting hookup type:', err);
      alert('Failed to delete. The hookup type may be in use by existing sites.');
    }
  };

  const handleEdit = (hookup: StoreHookupType) => {
    setEditingHookup(hookup);
    setEditForm({
      display_name: hookup.display_name,
      default_amp: hookup.default_amp?.toString() || '',
      display_order: hookup.display_order?.toString() || '',
      active: hookup.active
    });
    setEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingHookup) return;

    if (!editForm.display_name.trim()) {
      alert('Display name is required');
      return;
    }

    try {
      setSaving(true);
      const updateData: StoreHookupTypeUpdate = {
        display_name: editForm.display_name.trim(),
        default_amp: editForm.default_amp ? parseInt(editForm.default_amp, 10) : undefined,
        display_order: editForm.display_order ? parseInt(editForm.display_order, 10) : undefined,
        active: editForm.active
      };
      await siteService.updateStoreHookupType(editingHookup.id, updateData);
      await loadHookupTypes();
      setEditModalOpen(false);
      setEditingHookup(null);
    } catch (err) {
      console.error('Error updating hookup type:', err);
      alert('Failed to update hookup type. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setEditingHookup(null);
  };

  const handleOpenAddModal = () => {
    setAddForm({
      hookup_type: 'water',
      display_name: '',
      default_amp: '',
      display_order: '',
      active: true
    });
    setAddModalOpen(true);
  };

  const handleCloseAddModal = () => {
    setAddModalOpen(false);
  };

  const handleSaveAdd = async () => {
    try {
      setSaving(true);
      const createData: StoreHookupTypeCreate = {
        store_id: storeId,
        hookup_type: addForm.hookup_type,
        display_name: addForm.display_name.trim() || undefined,
        default_amp: addForm.default_amp ? parseInt(addForm.default_amp, 10) : undefined,
        display_order: addForm.display_order ? parseInt(addForm.display_order, 10) : undefined,
        active: addForm.active
      };
      await siteService.createStoreHookupType(createData);
      await loadHookupTypes();
      setAddModalOpen(false);
    } catch (err) {
      console.error('Error creating hookup type:', err);
      alert('Failed to create hookup type. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="config-card">
      <div
        className="config-card-header"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="config-card-title">
          <span className="config-card-icon">🔌</span>
          <h2>Hookup Types</h2>
        </div>
        <div className="config-card-header-right">
          <span className="config-card-db-label">campground_db/store_hookup_types</span>
          <span className={`config-card-toggle ${isExpanded ? 'expanded' : ''}`}>
            ▼
          </span>
        </div>
      </div>

      {isExpanded && (
        <div className="config-card-content">
          {loading ? (
            <div className="config-loading">Loading hookup types...</div>
          ) : error ? (
            <div className="config-error">{error}</div>
          ) : hookupTypes.length === 0 ? (
            <div className="config-empty">
              <p>No hookup types configured. Add hookups like water, electric, and sewer.</p>
              <button className="btn btn-primary btn-sm" onClick={handleOpenAddModal}>+ Add Hookup Type</button>
            </div>
          ) : (
            <>
              <table className="config-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Display Name</th>
                    <th>Default Amp</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {hookupTypes.map(hookup => (
                    <tr key={hookup.id}>
                      <td>
                        <span className="hookup-icon-cell">
                          <span className="hookup-icon">{getHookupIcon(hookup.hookup_type)}</span>
                          {getHookupLabel(hookup.hookup_type)}
                        </span>
                      </td>
                      <td>{hookup.display_name}</td>
                      <td>
                        {hookup.hookup_type === 'electricity' && hookup.default_amp
                          ? `${hookup.default_amp}A`
                          : '-'}
                      </td>
                      <td className={hookup.active ? 'status-active' : 'status-inactive'}>
                        {hookup.active ? 'Active' : 'Inactive'}
                      </td>
                      <td>
                        <div className="config-actions">
                          <button
                            className="btn btn-text btn-sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(hookup);
                            }}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-text btn-sm btn-danger"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(hookup.id, hookup.display_name);
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="config-card-footer">
                <button className="btn btn-primary btn-sm" onClick={handleOpenAddModal}>+ Add Hookup Type</button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Edit Modal */}
      <Modal
        isOpen={editModalOpen}
        onClose={handleCloseEditModal}
        title={`Edit Hookup: ${editingHookup ? getHookupLabel(editingHookup.hookup_type) : ''}`}
        size="small"
        footer={
          <>
            <button className="btn btn-secondary" onClick={handleCloseEditModal} disabled={saving}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleSaveEdit} disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </>
        }
      >
        <div className="edit-form">
          <div className="form-group">
            <label htmlFor="hookup-display-name">Display Name *</label>
            <input
              id="hookup-display-name"
              type="text"
              value={editForm.display_name}
              onChange={(e) => setEditForm(prev => ({ ...prev, display_name: e.target.value }))}
              placeholder="Enter display name"
            />
          </div>
          {editingHookup?.hookup_type === 'electricity' && (
            <div className="form-group">
              <label htmlFor="hookup-default-amp">Default Amp</label>
              <input
                id="hookup-default-amp"
                type="number"
                value={editForm.default_amp}
                onChange={(e) => setEditForm(prev => ({ ...prev, default_amp: e.target.value }))}
                placeholder="e.g., 30, 50"
              />
            </div>
          )}
          <div className="form-group">
            <label htmlFor="hookup-display-order">Display Order</label>
            <input
              id="hookup-display-order"
              type="number"
              value={editForm.display_order}
              onChange={(e) => setEditForm(prev => ({ ...prev, display_order: e.target.value }))}
              placeholder="e.g., 1, 2, 3"
            />
          </div>
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={editForm.active}
                onChange={(e) => setEditForm(prev => ({ ...prev, active: e.target.checked }))}
              />
              Active
            </label>
            <small className="form-help">Inactive hookup types will not be available for new sites.</small>
          </div>
        </div>
      </Modal>

      {/* Add Modal */}
      <Modal
        isOpen={addModalOpen}
        onClose={handleCloseAddModal}
        title="Add Hookup Type"
        size="small"
        footer={
          <>
            <button className="btn btn-secondary" onClick={handleCloseAddModal} disabled={saving}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleSaveAdd} disabled={saving}>
              {saving ? 'Adding...' : 'Add Hookup Type'}
            </button>
          </>
        }
      >
        <div className="edit-form">
          <div className="form-group">
            <label htmlFor="add-hookup-type">Hookup Type *</label>
            <select
              id="add-hookup-type"
              value={addForm.hookup_type}
              onChange={(e) => setAddForm(prev => ({ ...prev, hookup_type: e.target.value as HookupType }))}
            >
              {HOOKUP_TYPES.map(type => (
                <option key={type} value={type}>
                  {HOOKUP_ICONS[type]} {HOOKUP_LABELS[type]}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="add-hookup-display-name">Display Name</label>
            <input
              id="add-hookup-display-name"
              type="text"
              value={addForm.display_name}
              onChange={(e) => setAddForm(prev => ({ ...prev, display_name: e.target.value }))}
              placeholder="Leave blank to use default"
            />
          </div>
          {addForm.hookup_type === 'electricity' && (
            <div className="form-group">
              <label htmlFor="add-hookup-default-amp">Default Amp</label>
              <input
                id="add-hookup-default-amp"
                type="number"
                value={addForm.default_amp}
                onChange={(e) => setAddForm(prev => ({ ...prev, default_amp: e.target.value }))}
                placeholder="e.g., 30, 50"
              />
            </div>
          )}
          <div className="form-group">
            <label htmlFor="add-hookup-display-order">Display Order</label>
            <input
              id="add-hookup-display-order"
              type="number"
              value={addForm.display_order}
              onChange={(e) => setAddForm(prev => ({ ...prev, display_order: e.target.value }))}
              placeholder="e.g., 1, 2, 3"
            />
          </div>
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={addForm.active}
                onChange={(e) => setAddForm(prev => ({ ...prev, active: e.target.checked }))}
              />
              Active
            </label>
            <small className="form-help">Set as active to make this hookup type available for new sites.</small>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default HookupTypesCard;
